import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronRight, Search, X } from 'lucide-react'

export interface RecipeOption {
  cardId: number
  title: string
  /** Обложка рецепта. Пустая строка — карточка не загружена, покажем заглушку. */
  icon?: string
}

interface RecipePickerProps {
  options: readonly RecipeOption[]
  /** Выбранный рецепт или null, если размеры вводятся вручную. */
  value: number | null
  onChange: (cardId: number | null) => void
  /**
   * Открыт ли список. Состояние поднято на страницу: пока он открыт, кнопка
   * «Назад» в Telegram должна закрывать его, а не уводить с калькулятора.
   */
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

const MANUAL_LABEL = 'Свои размеры'

/**
 * Выбор рецепта: карточка с обложкой и названием открывает экран со списком.
 *
 * Раньше это был выпадающий список одними названиями — приходилось листать
 * вслепую и вспоминать, как называется нужный торт. Список с обложками, как на
 * главной, узнаётся с одного взгляда. Обложка выбранного рецепта живёт в самой
 * кнопке: отдельным превью под ней она повторяла бы название дважды.
 */
const RecipePicker = ({
  options,
  value,
  onChange,
  isOpen,
  onOpenChange,
}: RecipePickerProps) => {
  const [query, setQuery] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)

  const selected = options.find(option => option.cardId === value)

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return options
    return options.filter(option => option.title.toLowerCase().includes(needle))
  }, [options, query])

  // Пока список открыт, страница под ним не должна прокручиваться: иначе
  // после закрытия человек оказывается не там, где был.
  useEffect(() => {
    if (!isOpen) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    searchRef.current?.focus()
    return () => {
      document.body.style.overflow = previous
    }
  }, [isOpen])

  const close = () => {
    onOpenChange(false)
    setQuery('')
  }

  const select = (cardId: number | null) => {
    onChange(cardId)
    close()
  }

  return (
    <div className="shape-calc-recipe">
      <button
        type="button"
        className="shape-calc-recipe__card"
        onClick={() => onOpenChange(true)}
      >
        {selected?.icon ? (
          <img
            src={selected.icon}
            alt=""
            loading="lazy"
            className="shape-calc-recipe__cover"
          />
        ) : (
          <span
            className={`shape-calc-recipe__cover ${
              selected ? '' : 'shape-calc-recipe__cover--manual'
            }`}
          />
        )}

        <span className="shape-calc-recipe__text">
          <span className="shape-calc-recipe__label">Рецепт</span>
          <span className="shape-calc-recipe__title">
            {selected ? selected.title : MANUAL_LABEL}
          </span>
        </span>

        <ChevronRight size={18} aria-hidden />
      </button>

      {isOpen && (
        <div className="shape-calc-sheet" role="dialog" aria-modal="true">
          <div className="shape-calc-sheet__head">
            <h2>Выберите рецепт</h2>
            <button type="button" onClick={close} aria-label="Закрыть">
              <X size={20} />
            </button>
          </div>

          <label className="shape-calc-sheet__search">
            <Search size={18} aria-hidden />
            <input
              ref={searchRef}
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Поиск по названию"
              // 16px у поля — иначе iOS зумит страницу на фокусе.
              type="search"
            />
          </label>

          <div className="shape-calc-sheet__list">
            <button
              type="button"
              className={`shape-calc-sheet__item ${value === null ? 'active' : ''}`}
              onClick={() => select(null)}
            >
              <span className="shape-calc-sheet__thumb shape-calc-sheet__thumb--manual" />
              <span>{MANUAL_LABEL}</span>
            </button>

            {filtered.map(option => (
              <button
                key={option.cardId}
                type="button"
                className={`shape-calc-sheet__item ${option.cardId === value ? 'active' : ''}`}
                onClick={() => select(option.cardId)}
              >
                {option.icon ? (
                  <img
                    src={option.icon}
                    alt=""
                    loading="lazy"
                    className="shape-calc-sheet__thumb"
                  />
                ) : (
                  <span className="shape-calc-sheet__thumb" />
                )}
                <span>{option.title}</span>
              </button>
            ))}

            {filtered.length === 0 && (
              <p className="shape-calc-sheet__empty">
                По запросу «{query}» рецептов не нашлось
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default RecipePicker
